import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Upload, FileJson, Trash2, Users, Trophy, Clock,
  ChevronDown, ChevronUp, AlertCircle, CheckCircle2, Loader2,
  Medal, UserCheck, BarChart3,
} from 'lucide-react';
import { StatCard, SearchInput, RaceSelector, DataTable, FilterBar } from '@/components/shared';
import type { ResultsManagerViewProps } from './resultsmanager.props';

function ResultDetail({ result: r }: { result: any }) {
  const splits = r.splitsJson ? (() => { try { return JSON.parse(r.splitsJson); } catch { return null; } })() : null;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
      {r.club && <Field label="Клуб" value={r.club} />}
      {r.city && <Field label="Град" value={r.city} />}
      {r.yearOfBirth && <Field label="Год. раѓање" value={r.yearOfBirth} />}
      {r.pace && <Field label="Пејс" value={r.pace} />}
      {r.ageGroupName && <Field label="Возр. група" value={r.ageGroupName} />}
      {r.teamName && <Field label="Тим" value={r.teamName} />}
      {r.externalId && <Field label="Ext. ID" value={r.externalId} />}
      {splits && (
        <div className="col-span-full">
          <span className="text-muted-foreground text-xs">Кругови:</span>
          <div className="flex gap-2 flex-wrap mt-1">
            {splits.map((s: any, i: number) => (
              <Badge key={i} variant="outline" className="text-xs font-mono">{s}</Badge>
            ))}
          </div>
        </div>
      )}
      {(r.photoLink || r.videoLink) && (
        <div className="col-span-full flex gap-3">
          {r.photoLink && <a href={r.photoLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-xs">📷 Фото</a>}
          {r.videoLink && <a href={r.videoLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-xs">🎥 Видео</a>}
        </div>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: any }) {
  return (
    <div>
      <span className="text-muted-foreground text-xs">{label}</span>
      <div className="font-medium">{value}</div>
    </div>
  );
}

function ImportSection({ fileInputRef, importing, importResult, pasteValue, setPasteValue, onFileUpload, onPasteImport }: {
  fileInputRef: React.RefObject<HTMLInputElement>;
  importing: boolean;
  importResult: any;
  pasteValue: string;
  setPasteValue: (v: string) => void;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPasteImport: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Upload className="h-4 w-4" /> Импорт на резултати
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <label className="block text-sm font-medium mb-1">Прикачи JSON датотека(и)</label>
            <Input
              ref={fileInputRef}
              type="file"
              accept=".json"
              multiple
              onChange={onFileUpload}
              disabled={importing}
            />
          </div>
          <div className="text-center self-end text-sm text-muted-foreground px-2">или</div>
          <div className="flex-1">
            <label className="block text-sm font-medium mb-1">Залепи JSON</label>
            <div className="flex gap-2">
              <Input
                placeholder="Залепи го JSON содржината..."
                value={pasteValue}
                onChange={e => setPasteValue(e.target.value)}
                disabled={importing}
              />
              <Button onClick={onPasteImport} disabled={importing || !pasteValue.trim()} size="sm">
                {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileJson className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </div>

        {importing && (
          <div className="flex items-center gap-2 text-sm text-blue-600">
            <Loader2 className="h-4 w-4 animate-spin" /> Се импортираат резултатите...
          </div>
        )}

        {importResult && !importResult.error && (
          <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm space-y-1">
            <div className="flex items-center gap-2 font-medium text-green-800">
              <CheckCircle2 className="h-4 w-4" /> Импортот заврши успешно
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-green-700">
              <span>Креирани: <strong>{importResult.created}</strong></span>
              <span>Ажурирани: <strong>{importResult.updated}</strong></span>
              <span>Тимови креирани: <strong>{importResult.teamsCreated}</strong></span>
              <span>Тимови ажурирани: <strong>{importResult.teamsUpdated}</strong></span>
            </div>
            {importResult.detectedType && (
              <Badge variant="outline" className="mt-1">
                Детектиран тип: {importResult.detectedType}
              </Badge>
            )}
            {importResult.warnings?.length > 0 && (
              <div className="mt-2 space-y-1">
                {importResult.warnings.map((w: string, i: number) => (
                  <div key={i} className="flex items-start gap-1 text-amber-700 text-xs">
                    <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" /> {w}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {importResult?.error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Грешка: {typeof importResult.error === 'string' ? importResult.error : JSON.stringify(importResult.error)}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const ResultsManagerView: React.FC<ResultsManagerViewProps> = ({
  races, selectedRaceId, handleRaceChange, summary, tab, setTab, loading,
  raceTypes, filters, setFilters, setPage, individualColumns, results,
  expandedRow, setExpandedRow, page, totalPages, total, teams,
  fileInputRef, importing, importResult, pasteValue, setPasteValue,
  handleFileUpload, handlePasteImport, handleClearAll, embedded,
}) => {
  return (
    <div className="space-y-6">
      {!embedded && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5" /> Управување со резултати
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RaceSelector
              races={races}
              value={selectedRaceId}
              onChange={handleRaceChange}
              placeholder="Избери трка..."
              className="max-w-md"
            />
          </CardContent>
        </Card>
      )}

      {selectedRaceId && (
        <>
          <ImportSection
            fileInputRef={fileInputRef}
            importing={importing}
            importResult={importResult}
            pasteValue={pasteValue}
            setPasteValue={setPasteValue}
            onFileUpload={handleFileUpload}
            onPasteImport={handlePasteImport}
          />

          {summary && summary.totalFinishers > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
              <StatCard icon={<UserCheck className="h-5 w-5 text-green-600" />} label="Финиширале" value={summary.totalFinishers} />
              <StatCard icon={<Users className="h-5 w-5 text-blue-600" />} label="Машки" value={summary.maleCount} />
              <StatCard icon={<Users className="h-5 w-5 text-pink-600" />} label="Женски" value={summary.femaleCount} />
              <StatCard icon={<Clock className="h-5 w-5 text-amber-600" />} label="Најбрзо време" value={summary.fastestTime || '-'} sub={summary.fastestName} />
              <StatCard icon={<Medal className="h-5 w-5 text-purple-600" />} label="Тимови" value={summary.totalTeams} />
              <StatCard icon={<BarChart3 className="h-5 w-5 text-indigo-600" />} label="Возр. групи" value={summary.ageGroups?.length || 0} />
            </div>
          )}

          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <Tabs value={tab} onValueChange={t => { setTab(t); setPage(1); }}>
                  <TabsList>
                    <TabsTrigger value="individual">Индивидуални</TabsTrigger>
                    <TabsTrigger value="teams">Тимови</TabsTrigger>
                  </TabsList>
                </Tabs>

                <div className="flex items-center gap-2">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm" disabled={!summary?.totalFinishers}>
                        <Trash2 className="h-4 w-4 mr-1" /> Избриши ги сите
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Избриши ги сите резултати?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Ова ќе ги избрише сите резултати и тимски резултати за оваа трка. Ова дејство не може да се врати.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Откажи</AlertDialogCancel>
                        <AlertDialogAction onClick={handleClearAll}>Избриши</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {tab === 'individual' && (
                <FilterBar>
                  {raceTypes.length > 0 && (
                    <Select value={filters.raceTypeId} onValueChange={v => setFilters(f => ({ ...f, raceTypeId: v === 'all' ? '' : v }))}>
                      <SelectTrigger className="w-[160px]">
                        <SelectValue placeholder="Тип на трка" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Сите типови</SelectItem>
                        {raceTypes.map((rt: any) => (
                          <SelectItem key={rt.id} value={String(rt.id)}>{rt.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Select value={filters.gender} onValueChange={v => setFilters(f => ({ ...f, gender: v === 'all' ? '' : v }))}>
                    <SelectTrigger className="w-[120px]">
                      <SelectValue placeholder="Пол" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Сите</SelectItem>
                      <SelectItem value="M">Машки</SelectItem>
                      <SelectItem value="F">Женски</SelectItem>
                    </SelectContent>
                  </Select>
                  {summary?.ageGroups?.length > 0 && (
                    <Select value={filters.ageGroup} onValueChange={v => setFilters(f => ({ ...f, ageGroup: v === 'all' ? '' : v }))}>
                      <SelectTrigger className="w-[160px]">
                        <SelectValue placeholder="Возр. група" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Сите групи</SelectItem>
                        {summary.ageGroups.map((ag: string) => (
                          <SelectItem key={ag} value={ag}>{ag}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <SearchInput
                    value={filters.search}
                    onChange={(v: string) => { setFilters(f => ({ ...f, search: v })); setPage(1); }}
                    placeholder="Пребарај по име или број..."
                    className="flex-1 min-w-[200px]"
                  />
                </FilterBar>
              )}

              {tab === 'individual' ? (
                <DataTable
                  columns={individualColumns}
                  data={results}
                  loading={loading}
                  expandedRowId={expandedRow}
                  onExpandToggle={(id: any) => setExpandedRow(expandedRow === id ? null : id)}
                  renderExpanded={(r: any) => <ResultDetail result={r} />}
                  page={page}
                  totalPages={totalPages}
                  total={total}
                  onPageChange={setPage}
                  emptyMessage={filters.search ? 'Нема резултати за пребарувањето' : 'Нема резултати'}
                />
              ) : (
                <div className="space-y-3">
                  {teams.map((t: any) => (
                    <Card key={t.id} className="overflow-hidden">
                      <div
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                        onClick={() => setExpandedRow(expandedRow === `t-${t.id}` ? null : `t-${t.id}`)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold text-sm">
                            {t.teamRank || '-'}
                          </div>
                          <div>
                            <div className="font-medium">{t.teamName}</div>
                            <div className="text-xs text-muted-foreground">
                              {t.memberCount} членови · {t.totalTime || 'N/A'}
                            </div>
                          </div>
                        </div>
                        {expandedRow === `t-${t.id}` ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </div>
                      {expandedRow === `t-${t.id}` && t.members?.length > 0 && (
                        <div className="border-t px-4 py-2">
                          <table className="w-full text-sm">
                            <tbody>
                              {t.members.map((m: any) => (
                                <tr key={m.id} className="border-b last:border-b-0">
                                  <td className="py-1.5 font-mono">{m.bibNumber}</td>
                                  <td className="py-1.5">{m.fullName}</td>
                                  <td className="py-1.5 font-mono">{m.chipTime || m.gunTime || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </Card>
                  ))}
                  {teams.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">Нема тимски резултати</div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default ResultsManagerView;
